import React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IdTaskDto, TaskDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { archiveTasks } from '@/services/ws-funnel/src/main/resources/rest-api';
import { SelectField } from '@/js/libs/form';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { ModalStateOpen } from '@/js/libs/hooks/useModalState';
import type { SelectionState } from '@/js/libs/hooks/useSelectionState';
import { Button, Modal } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification';
import { useSuccessNotification } from 'src/hooks/useSuccessNotification';
import { useTranslateOptionLabel } from 'src/hooks/useTranslateOptionLabel';
import { PossibleOutcomes } from './constants';
import type { FormValues } from './schema';

interface ArchiveModalProps {
  state: ModalStateOpen<IdTaskDto[]>;
  form: UseFormReturn<FormValues>;
  selection: SelectionState<TaskDto, IdTaskDto>;
  reloadTasks: () => void;
}

const outcomeOptions = PossibleOutcomes.map((outcome) => ({
  label: outcome,
  value: outcome,
}));

export const ArchiveModal: React.FC<ArchiveModalProps> = React.memo(function ArchiveModal({ state, form, selection, reloadTasks }) {
  const [t] = useTranslation(['backend', 'common', 'serviceDashboard', 'messages']);
  const formatOptionLabel = useTranslateOptionLabel(t, 'serviceDashboard');
  const onFail = useRequestErrorNotification();

  const archiveRequest = useLazyRequest({
    apiMethod: archiveTasks,
    onSuccess: useSuccessNotification(t('serviceDashboard:TASK_WAS_ARCHIVED')),
    onFail,
  });

  const {
    control,
    formState: { isSubmitting },
  } = form;

  const onSubmit = useCallbackRef(async (values: FormValues) => {
    if (state.payload && values.outcome) {
      await archiveRequest.load({ taskIds: state.payload, outcome: values.outcome.value });
      selection.deselectAll();
      reloadTasks();
      state.close();
    }
  });

  return (
    <Modal state={state} title={t('serviceDashboard:ARCHIVE_TASK')}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Modal.Body data-testid='reason-form'>
            <SelectField
              control={control}
              name='outcome'
              options={outcomeOptions}
              label={t('serviceDashboard:REASON')}
              placeholder={t('common:ENTER')}
              formatOptionLabel={formatOptionLabel}
              isClearable
              required
            />
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={state.close} ghost disabled={isSubmitting} data-testid='cancel-button'>
              {t('common:CANCEL')}
            </Button>
            <Button primary type='submit' disabled={isSubmitting} isLoading={isSubmitting} data-testid='submit-button'>
              {t('serviceDashboard:ARCHIVE')}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
});

export { type FormValues as ArchiveModalFormValues, createSchema as archiveModalCreateSchema } from './schema';
